import React, { Component } from 'react'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'
import Helmet from 'react-helmet'
import _cloneDeep from 'lodash/cloneDeep'
import _values from 'lodash/values'
import _each from 'lodash/each'
import _get from 'lodash/get'
import _uniq from 'lodash/uniq'

import getContentType from './utils/getContentType'
import slugify from './utils/slugify'
import { fetchAllContentTypes } from './utils/fetch'
import {
  getColours,
  getCategories,
  getConservationStatus
} from './utils/filterUtils'
import { watchGeolocation } from './utils/geolocation'
import globalStyles, { AppWrap } from './globalStyles'
import ScrollToTop from './components/ScrollToTop'
import ServiceWorkerNotifications from './components/ServiceWorkerNotifications'
import GoogleAnalytics from './components/GoogleAnalytics'
import Loading from './components/Loading'
import Nav from './components/Nav'
import Footer from './components/Footer'
import Home from './views/Home'
import TrackList from './views/TrackList'
import LocationSingle from './views/LocationSingle'
import TrackSingle from './views/TrackSingle'
import SpeciesList from './views/SpeciesList'
import SpeciesSingle from './views/SpeciesSingle'
import Glossary from './views/Glossary'
import InfoPage from './views/InfoPage'
import More from './views/More'
import YourLocation from './views/YourLocation'
import Alerts from './views/Alerts'
import LeaveFeedback from './views/LeaveFeedback'
import MapView from './views/MapView'
import NoMatch from './views/NoMatch'
import PassInput from './views/Pass'

import data from './data.json'

class App extends Component {
  constructor (props) {
    super(props)

    const filters = this.setupFilters([...data.flora, ...data.fauna])

    this.state = {
      appLoading: false,
      appVersion: data.app && data.app.version,
      locations: data.locations,
      flora: data.flora,
      fauna: data.fauna,
      species: [],
      pages: data.pages,
      infoPages: data.infoPages,
      glossary: data.glossary,
      tracks: data.tracks,
      protected: !this.checkPasswordCookie(),
      previewUpdated: false,
      filters: filters,
      filtersInit: _cloneDeep(filters),
      touchedFilters: {
        flora: 0,
        fauna: 0
      },
      geolocation: null,
      alerts: this.mapAlertsData(data.alerts) || [
        {
          title: 'Alert!',
          type: 'error',
          timeRange: [600, 1800],
          message: 'This is an alert...'
        },
        {
          title: 'Info Title',
          type: 'info',
          message: 'This is an info'
        },
        {
          title: 'Warning',
          type: 'warning',
          message: 'This is a warning'
        },
        {
          title: 'Success',
          type: 'success',
          message: 'This is a success'
        }
      ]
    }
  }

  componentWillMount () {
    globalStyles()
  }

  componentDidMount () {
    if (this.state.appVersion) {
      console.log(`Discover Springbrook version ${this.state.appVersion}`)
    }
    this.updatePreviewData()
  }

  componentDidUpdate () {
    this.updatePreviewData()
  }

  updatePreviewData () {
    const {
      REACT_APP_CONTENTFUL_PREVIEW_TOKEN: previewToken,
      REACT_APP_CONTENTFUL_ID: spaceID
    } = process.env

    if (
      !previewToken ||
      !spaceID ||
      this.state.protected ||
      this.state.previewUpdated ||
      this.state.appLoading
    ) {
      return false
    }

    console.log('Dev build')
    this.setState({ appLoading: true })

    fetchAllContentTypes(spaceID, previewToken, true).then(data => {
      if (!data) return null
      const {
        alerts,
        fauna,
        flora,
        glossary,
        infoPages,
        locations,
        pages,
        tracks
      } = data

      this.setState({
        alerts: this.mapAlertsData(alerts),
        fauna,
        flora,
        glossary,
        infoPages,
        locations,
        pages,
        tracks,
        previewUpdated: true,
        appLoading: false
      })
    })
  }

  mapAlertsData = alertsData =>
    alertsData.map(({ fields, sys }) => ({
      ...fields,
      id: sys.id,
      createdAt: sys.createdAt,
      updatedAt: sys.updatedAt
    }))

  checkPasswordCookie = () => {
    if (
      !process.env.REACT_APP_CONTENTFUL_PREVIEW_TOKEN ||
      !window.localStorage
    ) {
      return true
    }
    const now = new Date()
    this.dateNum = now.getFullYear() + now.getMonth() + now.getDate()
    return window.localStorage.getItem('checkds') === `ds${this.dateNum}`
  }

  updateGeolocation = () =>
    watchGeolocation({
      onSuccess: position => {
        this.setState({ geolocation: { error: false, position } })
      },
      onFailure: err => {
        console.error('Error fetching geolocation:', err)
        this.setState({ geolocation: { error: err } })
      }
    })

  setupFilters = species => {
    const filters = {
      flora: {
        categories: getCategories(species, 'flora'),
        colours: getColours(species, 'flora'),
        conservationStatus: getConservationStatus(species, 'flora')
      },
      fauna: {
        categories: getCategories(species, 'fauna'),
        colours: getColours(species, 'fauna'),
        conservationStatus: getConservationStatus(species, 'fauna')
      }
    }
    return filters
  }

  updateFilter = (type, key, filters) => {
    const filterIsEmpty =
      _values(this.state.filters[type][key]).indexOf(false) < 0
    const currentFilters = _cloneDeep(this.state.filters)
    Object.assign(currentFilters[type][key], filters)
    if (filterIsEmpty) {
      // reverse values if filterIsEmpty
      _each(currentFilters[type][key], (value, innerKey) => {
        currentFilters[type][key][innerKey] = !value
      })
    }
    const touchedFilters = this.calculateActiveFilters(
      currentFilters,
      this.state.filtersInit
    )
    this.setState({
      filters: currentFilters,
      touchedFilters: touchedFilters
    })
  }

  calculateActiveFilters = (nextFilters, initialFilters) => {
    // recursively compare nextFilter with initialFilters
    let touchedFilters = { flora: [], fauna: [] }
    _each(nextFilters, (type, typeKey) => {
      _each(type, (filter, filterKey) => {
        _each(filter, (value, key) => {
          const initialValue = _get(
            initialFilters,
            `[${typeKey}][${filterKey}][${key}]`
          )
          if (initialValue !== value) touchedFilters[typeKey].push(filterKey)
        })
      })
    })
    // reduce to number of filters touched
    _each(touchedFilters, (type, typeKey) => {
      touchedFilters[typeKey] = _uniq(type).length
    })
    return touchedFilters
  }

  resetFilters = () => {
    this.setState({
      filters: _cloneDeep(this.state.filtersInit),
      touchedFilters: { flora: 0, fauna: 0 }
    })
  }

  linkContentRelations = content => {
    const currentLocation = window.location.pathname

    // keep track of replacement history, ignore substrings of existing replacements
    let prevReplacements = []

    const alreadyReplaced = search => {
      return prevReplacements.filter(str => {
        const alreadyReplaced = str.indexOf(search.toLowerCase()) >= 0
        return alreadyReplaced
      }).length
    }

    const sortByLengthDesc = (a, b) =>
      a.fields.title.length > b.fields.title.length ? -1 : 1

    const makeReplacement = (search, replacementLink) => {
      if (
        !search ||
        currentLocation.indexOf(replacementLink) >= 0 ||
        alreadyReplaced(search)
      ) {
        // skip if window is currently at this link, avoid linking to self
        return content
      }

      const regex = new RegExp(`${search}[s]?`, 'i')
      content = content.replace(regex, match => {
        // push to replacement history
        prevReplacements.push(search.toLowerCase())
        return `[${match}](${replacementLink})`
      })
      return content
    }

    const species = [...this.state.flora, ...this.state.fauna]

    species.forEach(item => {
      const type = getContentType(item)
      makeReplacement(item.fields.title, `/${type}/${item.fields.slug}`)
      makeReplacement(item.fields.speciesName, `/${type}/${item.fields.slug}`)
    })

    this.state.locations.forEach(item =>
      makeReplacement(item.fields.title, `/locations/${item.fields.slug}`)
    )

    this.state.tracks.forEach(item =>
      makeReplacement(item.fields.title, `/walks/${item.fields.slug}`)
    )

    this.state.glossary
      .sort(sortByLengthDesc)
      .forEach(item =>
        makeReplacement(
          item.fields.title,
          `/glossary/#${slugify(item.fields.title)}`
        )
      )

    return content
  }

  routeWithProps = (Component, route, props) => {
    return (
      <AppWrap>
        {props &&
          props.backButton && (
            <Nav
              backButton
              route={route}
              parentPath={props && props.parentPath}
            />
          )}
        <Nav
          route={route}
          tracks={this.state.tracks}
          flora={this.state.flora}
          fauna={this.state.fauna}
        />
        <Component
          {...props}
          match={route.match}
          pages={this.state.pages}
          infoPages={this.state.infoPages}
          locations={this.state.locations}
          tracks={this.state.tracks}
          glossary={this.state.glossary}
          species={[...this.state.flora, ...this.state.fauna]}
          type={props && props.type}
          filters={this.state.filters}
          filtersInit={this.state.filtersInit}
          updateFilter={this.updateFilter}
          resetFilters={this.resetFilters}
          touchedFilters={this.state.touchedFilters}
          geolocation={this.state.geolocation}
          updateGeolocation={this.updateGeolocation}
          alerts={this.state.alerts}
          appVersion={this.state.appVersion}
          linkContentRelations={this.linkContentRelations}
          route={route}
        />
        <Footer
          glossary={this.state.glossary}
          infoPages={this.state.infoPages}
          appVersion={this.state.appVersion}
        />
      </AppWrap>
    )
  }

  checkPassword = e => {
    if (e.target.value === 'ds' && window.localStorage) {
      this.setState({ protected: false })
      window.localStorage.setItem('checkds', `ds${this.dateNum}`)
    }
  }

  render () {
    if (this.state.protected) return <PassInput onChange={this.checkPassword} />
    if (this.state.appLoading) return <Loading />

    const landingPage = this.state.pages.find(
      page => page.fields.slug === 'landing-page'
    )

    return (
      <Router>
        <div className='React-Wrap'>
          <ScrollToTop />
          <ServiceWorkerNotifications reloadOnUpdate />
          <GoogleAnalytics id='UA-113650009-1' />

          <Helmet
            defaultTitle={landingPage.fields.title}
            titleTemplate={`%s | ${landingPage.fields.title}`}
          />

          <Switch>
            <Route
              exact
              path='/'
              render={route =>
                this.routeWithProps(Home, route, { page: landingPage })
              }
            />
            {this.state.tracks.length && (
              <Route
                exact
                path='/walks'
                render={route => this.routeWithProps(TrackList, route)}
              />
            )}
            {this.state.tracks.length && (
              <Route
                path='/walks/:slug'
                render={route =>
                  this.routeWithProps(TrackSingle, route, {
                    parentPath: '/walks',
                    backButton: true
                  })
                }
              />
            )}
            {this.state.locations.length && (
              <Route
                path='/locations/:slug'
                render={route =>
                  this.routeWithProps(LocationSingle, route, {
                    backButton: true
                  })
                }
              />
            )}
            {this.state.flora.length && (
              <Route
                exact
                path='/flora'
                render={route =>
                  this.routeWithProps(SpeciesList, route, { type: 'flora' })
                }
              />
            )}
            {this.state.flora.length && (
              <Route
                path='/flora/:slug'
                render={route =>
                  this.routeWithProps(SpeciesSingle, route, {
                    parentPath: '/flora',
                    backButton: true
                  })
                }
              />
            )}
            {this.state.fauna.length && (
              <Route
                exact
                path='/fauna'
                render={route =>
                  this.routeWithProps(SpeciesList, route, { type: 'fauna' })
                }
              />
            )}
            {this.state.fauna.length && (
              <Route
                path='/fauna/:slug'
                render={route =>
                  this.routeWithProps(SpeciesSingle, route, {
                    parentPath: '/fauna',
                    backButton: true
                  })
                }
              />
            )}
            <Route
              path='/map'
              exact
              render={route =>
                this.routeWithProps(MapView, route, { backButton: true })
              }
            />
            {this.state.glossary.length && (
              <Route
                path='/glossary'
                exact
                render={route =>
                  this.routeWithProps(Glossary, route, { backButton: true })
                }
              />
            )}
            <Route
              path='/more'
              exact
              render={route => this.routeWithProps(More, route)}
            />
            <Route
              path='/more/your-location'
              exact
              render={route =>
                this.routeWithProps(YourLocation, route, {
                  backButton: true,
                  parentPath: '/more'
                })
              }
            />
            <Route
              path='/more/alerts'
              exact
              render={route =>
                this.routeWithProps(Alerts, route, {
                  backButton: true,
                  parentPath: '/more'
                })
              }
            />
            <Route
              path='/more/leave-feedback'
              exact
              render={route =>
                this.routeWithProps(LeaveFeedback, route, {
                  backButton: true,
                  parentPath: '/more'
                })
              }
            />
            {this.state.infoPages &&
              this.state.infoPages.map(infoPage => (
                <Route
                  key={`Route-InfoPage-${infoPage.fields.slug}`}
                  path={`/${infoPage.fields.slug}`}
                  exact
                  render={route =>
                    this.routeWithProps(InfoPage, route, {
                      infoPage,
                      backButton: true
                    })
                  }
                />
              ))}
            <Route render={route => this.routeWithProps(NoMatch, route)} />
          </Switch>
        </div>
      </Router>
    )
  }
}

export default App



// WEBPACK FOOTER //
// ./src/App.js